import { ApiError, req } from '../src/api';
import type { Session } from '../src/types';

/** The admin API (LeadsIRL logins only). Shapes match src/handlers/portal-api.ts and src/lib/admin.ts. */

/** done = finished; pending = under way; broken = stopped working; todo = not started. */
export type StepState = 'done' | 'pending' | 'broken' | 'todo';
export interface Setup { number: StepState; tenDlc: StepState; calendar: StepState; logins: StepState; live: StepState }
export type TenDlc = 'not-started' | 'submitted' | 'approved' | 'rejected';

export interface ClientRow {
  clientId: string;
  businessName: string;
  industry?: string;
  city?: string;
  phoneNumber?: string;
  active: boolean;
  demo: boolean;
  liveAt?: string;
  createdAt?: string;
  setup: Setup;
  attention: string[];
  leads30: number;
  texts30: number;
  costMonthMicros: number;
  lastActiveAt?: string;
}

export interface UsageTotals {
  smsOut: number; smsIn: number; smsOutFailed: number; mmsOut: number; mmsIn: number;
  smsCostMicros: number; aiCalls: number; aiInTokens: number; aiOutTokens: number; aiCostMicros: number;
  calls: number; missedCalls: number; forwardedCalls: number; forwardSeconds: number; addressChecks: number; emails: number;
}
export type UsageDay = Partial<UsageTotals> & { day: string };

export interface ShopLogin {
  email: string;
  name?: string;
  clientId: string;
  role: 'owner' | 'staff';
  status: 'invited' | 'active' | 'reset' | 'other';
  enabled: boolean;
  createdAt?: string;
  lastActiveAt?: string;
}

export interface ShopConfig {
  clientId: string;
  businessName: string;
  industry?: string;
  city?: string;
  timezone: string;
  phoneNumber: string;
  ownerPhone?: string;
  notifyEmail?: string;
  tenDlcStatus?: TenDlc;
  liveAt?: string;
  active: boolean;
  createdAt?: string;
  bookingEnabled?: boolean;
  calendarProvider?: 'google' | 'outlook';
  calendarConnectedAt?: string;
  demo?: boolean;
}

export interface SupportVisit { email: string; hour: string; lastAt: string }

export interface ClientDetail {
  config: ShopConfig;
  setup: Setup;
  attention: string[];
  demo: boolean;
  logins: ShopLogin[];
  usage: { days: UsageDay[]; month: UsageTotals; last30: UsageTotals };
  leads30: number;
  support: SupportVisit[];
}

const shopPath = (id: string) => `/api/admin/clients/${encodeURIComponent(id)}`;
const loginPath = (id: string, email: string) => `${shopPath(id)}/logins/${encodeURIComponent(email)}`;
const send = (method: string, body?: unknown): RequestInit => ({ method, ...(body === undefined ? {} : { body: JSON.stringify(body) }) });

export async function listClients(s: Session): Promise<ClientRow[]> {
  return (await req<{ clients: ClientRow[] }>(s, '/api/admin/clients')).clients;
}

export function getClient(s: Session, id: string): Promise<ClientDetail> {
  return req<ClientDetail>(s, shopPath(id));
}

export interface NewShop { businessName: string; industry?: string; city?: string; timezone: string; notifyEmail?: string }
export async function addClient(s: Session, shop: NewShop): Promise<ShopConfig> {
  return (await req<{ config: ShopConfig }>(s, '/api/admin/clients', send('POST', shop))).config;
}

export interface ShopChange {
  businessName?: string; industry?: string; city?: string; phoneNumber?: string;
  tenDlcStatus?: TenDlc; live?: boolean; active?: boolean;
}
export async function updateClient(s: Session, id: string, change: ShopChange): Promise<void> {
  await req(s, shopPath(id), send('PUT', change));
}

export async function inviteLogin(s: Session, id: string, who: { email: string; name?: string; role: 'owner' | 'staff' }): Promise<void> {
  await req(s, `${shopPath(id)}/logins`, send('POST', who));
}

/** 'invite' = the invite went out again; 'reset' = they must choose a new password (a code is emailed). */
export async function resendLogin(s: Session, id: string, email: string): Promise<'invite' | 'reset'> {
  return (await req<{ sent: 'invite' | 'reset' }>(s, `${loginPath(id, email)}/resend`, send('POST'))).sent;
}

export async function changeLogin(s: Session, id: string, email: string, change: { enabled?: boolean; role?: 'owner' | 'staff' }): Promise<void> {
  await req(s, loginPath(id, email), send('PUT', change));
}

export async function removeLogin(s: Session, id: string, email: string): Promise<void> {
  await req(s, loginPath(id, email), send('DELETE'));
}

/** What went wrong, in the API's own words when it gave any. */
export function errorText(e: unknown): string {
  if (e instanceof ApiError) {
    if (e.body?.error) return e.body.error;
    if (e.status === 403) return 'Your login is not allowed to do that.';
    if (e.status >= 500) return 'The server had a problem. Try again in a minute.';
    return `Something went wrong (${e.status}).`;
  }
  if (e instanceof TypeError) return 'Could not reach the server. Check your connection.';
  return e instanceof Error ? e.message : 'Something went wrong.';
}
